"use client";

import {
  IconPeopleCircle,
  IconPostcard2,
  IconRobot2,
  IconStopwatch,
} from "@central-icons-react/round-outlined-radius-3-stroke-2";

import type { OrgMember } from "@/lib/org/queries";
import type { AgentSummary } from "@/lib/agents/types";
import type { TaskStatus } from "@/lib/tasks/queries";
import { cn } from "@/lib/utils";

import { TaskAccountMenu, AccountAvatar, type AccountOption } from "./task-account-menu";
import { TaskAssigneeMenu, MemberAvatar, memberLabel } from "./task-assignee-menu";
import { TaskChip } from "./task-chip";
import { TaskStartAtMenu, formatStartAtChipLabel } from "./task-start-at-menu";
import { TaskAgentMenu } from "./task-agent-menu";
import { STATUS_LABELS, TaskStatusIcon } from "./task-status-icon";
import { TaskStatusMenu } from "./task-status-menu";

/**
 * Pre-composed chip + menu pairs for the four task fields that show up
 * across all task surfaces (new-task modal, new-task composer, task detail
 * drawer, task detail page, task board cards).
 *
 * Each chip owns:
 *   - the popover trigger button (icon + label)
 *   - the filled / placeholder visual state
 *   - the title attribute for the tooltip
 *   - the popover menu itself
 *
 * Use these unless you genuinely need a different trigger appearance — in
 * which case reach for `<TaskXxxMenu trigger={<TaskChip>…</TaskChip>}>`
 * directly.
 */

const DEFAULT_LABEL_CLASS = "max-w-[12rem] truncate";

// ---------- Account ----------

type AccountChipProps = {
  value: string | null;
  onChange: (id: string | null) => void;
  accounts: AccountOption[];
  disabled?: boolean;
  /** Override truncation width. Defaults to `max-w-[12rem] truncate`. */
  labelClassName?: string;
  className?: string;
  avatarClassName?: string;
  /** When false, the placeholder state shows label only (no postcard icon). */
  showEmptyIcon?: boolean;
};

export function AccountChip({
  value,
  onChange,
  accounts,
  disabled,
  labelClassName,
  className,
  avatarClassName,
  showEmptyIcon = true,
}: AccountChipProps) {
  const current = accounts.find((a) => a.id === value) ?? null;
  return (
    <TaskAccountMenu
      value={value}
      onChange={onChange}
      accounts={accounts}
      disabled={disabled}
      trigger={
        <TaskChip
          filled={!!current}
          disabled={disabled}
          className={className}
          title={current ? `Account: ${current.name}` : "Set account"}
        >
          {current ? (
            <AccountAvatar account={current} className={avatarClassName} />
          ) : showEmptyIcon ? (
            <IconPostcard2 className="size-4 text-[#777]" aria-hidden />
          ) : null}
          <span className={cn(labelClassName ?? DEFAULT_LABEL_CLASS)}>
            {current ? current.name : "Account"}
          </span>
        </TaskChip>
      }
    />
  );
}

// ---------- Assignee ----------

type AssigneeChipProps = {
  value: string | null;
  onChange: (id: string | null) => void;
  members: OrgMember[];
  disabled?: boolean;
  labelClassName?: string;
  /** Placeholder label when no assignee is selected. */
  emptyLabel?: string;
};

export function AssigneeChip({
  value,
  onChange,
  members,
  disabled,
  labelClassName,
  emptyLabel = "Assignee",
}: AssigneeChipProps) {
  const current = members.find((m) => m.id === value) ?? null;
  const label = current ? memberLabel(current) : emptyLabel;
  return (
    <TaskAssigneeMenu
      value={value}
      onChange={onChange}
      members={members}
      disabled={disabled}
      trigger={
        <TaskChip
          filled={!!current}
          disabled={disabled}
          title={current ? `Assignee: ${label}` : "Set assignee"}
        >
          {current ? (
            <MemberAvatar member={current} />
          ) : (
            <IconPeopleCircle
              className="size-4 shrink-0 text-[#777]"
              aria-hidden
            />
          )}
          <span className={cn(labelClassName ?? DEFAULT_LABEL_CLASS)}>
            {label}
          </span>
        </TaskChip>
      }
    />
  );
}

// ---------- Status ----------

type StatusChipProps = {
  value: TaskStatus;
  onChange: (next: TaskStatus) => void;
  disabled?: boolean;
};

export function StatusChip({ value, onChange, disabled }: StatusChipProps) {
  return (
    <TaskStatusMenu
      value={value}
      onChange={onChange}
      disabled={disabled}
      trigger={
        <TaskChip
          filled
          disabled={disabled}
          title={`Status: ${STATUS_LABELS[value]}`}
        >
          <TaskStatusIcon status={value} size={16} />
          <span>{STATUS_LABELS[value]}</span>
        </TaskChip>
      }
    />
  );
}

// ---------- Start at ----------

type StartAtChipProps = {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /** Placeholder label when start mode is manual. */
  emptyLabel?: string;
};

export function StartAtChip({
  value,
  onChange,
  disabled,
  emptyLabel = "Manual",
}: StartAtChipProps) {
  const label = formatStartAtChipLabel(value, emptyLabel);
  return (
    <TaskStartAtMenu
      value={value}
      onChange={onChange}
      disabled={disabled}
      trigger={
        <TaskChip
          filled={value !== null}
          disabled={disabled}
          title={`Start: ${label}`}
        >
          <IconStopwatch
            className="size-4 shrink-0 text-[#777]"
            aria-hidden
          />
          <span>{label}</span>
        </TaskChip>
      }
    />
  );
}

// ---------- Agent ----------

type AgentChipProps = {
  value: string | null;
  onChange: (id: string | null) => void;
  agents: AgentSummary[];
  disabled?: boolean;
  labelClassName?: string;
  className?: string;
  /** When false, the chip shows label only (no agent icon). */
  showIcon?: boolean;
};

export function AgentChip({
  value,
  onChange,
  agents,
  disabled,
  labelClassName,
  className,
  showIcon = true,
}: AgentChipProps) {
  const current = agents.find((w) => w.id === value) ?? null;
  const filled = !!current;
  return (
    <TaskAgentMenu
      value={value}
      onChange={onChange}
      agents={agents}
      disabled={disabled}
      trigger={
        <TaskChip
          filled={filled}
          disabled={disabled}
          className={className}
          title={current ? `Agent: ${current.name}` : "Pick an agent"}
        >
          {showIcon ? (
            <IconRobot2
              className={cn(
                "size-4 shrink-0",
                filled ? "text-[#02abff]" : "text-[#777]",
              )}
              aria-hidden
            />
          ) : null}
          <span className={cn(labelClassName ?? DEFAULT_LABEL_CLASS)}>
            {current ? current.name : "Agent"}
          </span>
        </TaskChip>
      }
    />
  );
}
